import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDSBadge, IDSButton, IDSInput, IDSAlert } from "@inera/ids-react";
import { fetchContractsForOrg } from "../../../services/contractsAPI";
import { useAuth } from "../../../contexts/AuthContext";
import { useOrganisation } from "../../../contexts/OrganisationContext";
import { useContract } from "../../../contexts/ContractContext";
import { getTestExecutionStatusBadge } from "../../../utils/testExecutionStatus";

/**
 * Landing view for the Testklient.
 * Lists all service contracts available to the user's organisation as cards.
 * Clicking a card navigates to the test execution view for that contract.
 */
const ContractSelection = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { loading: authLoading, isAuthenticated } = useAuth();
  const { activeOrgId } = useOrganisation();
  const { setActiveContractId, setActiveContractName, setActiveContractVersion } = useContract();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || !isAuthenticated || !activeOrgId) return;

    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchContractsForOrg(activeOrgId);
        setContracts(data);
      } catch (err) {
        console.error("Failed to fetch contracts:", err);
        setError("Kunde inte hämta tjänstekontrakt. Försök ladda om sidan.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, isAuthenticated, activeOrgId]);

  const filtered = contracts
    .filter((c) => c.contractName.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.contractName.localeCompare(b.contractName, "sv"));

  const handleSelectContract = (contract) => {
    setActiveContractId(contract.id);
    setActiveContractName(contract.contractName);
    setActiveContractVersion(contract.version || null);
    navigate(`/testklient/${contract.id}`);
  };

  const getStatusBadge = (contract) => {
    if (contract.inReview) {
      // Same per-status label the test reports view uses, so the two never disagree
      // (e.g. this used to always show "Inväntar granskning" even while IN_CLARIFY).
      const badge = getTestExecutionStatusBadge(contract.reviewStatus);
      return <IDSBadge type={badge.type}>{badge.label}</IDSBadge>;
    }
    return <IDSBadge type="success">Redo att testa</IDSBadge>;
  };

  return (
    <div className="ids-mt-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: '1200px', width: '100%', margin: '2rem auto 0' }}>
        <h1 className="ids-heading-l">Testklient</h1>
        <p className="ids-body ids-mb-6">
          Här hittar du alla tjänstekontrakt som din organisation har beställt. Välj ett kontrakt
          för att konfigurera parametrar, köra testfall och sedan skapa en testrapport. Kontrakt
          som redan har en rapport inskickad för granskning är markerade – du kan fortfarande köra
          tester men inte skicka in en ny rapport förrän granskningen är klar. Dessa testsviter
          används för att verifiera implementationen av tjänstekontrakt inför integration med
          nationell tjänsteplattform.
        </p>

        <div style={{ maxWidth: "400px", marginBottom: "2rem" }}>
          <IDSInput
            icon="search"
            type="text"
            placeholder="Sök tjänstekontrakt..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loading && <p>Laddar tjänstekontrakt...</p>}
        {error && (
          <IDSAlert type="error" className="ids-mb-4">
            <p>{error}</p>
          </IDSAlert>
        )}

        {!loading && !error && filtered.length === 0 && (
          <p className="ids-body">Inga tjänstekontrakt hittades.</p>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1.5rem",
            }}
          >
            {filtered.map((contract) => (
              <div
                key={contract.id}
                style={{
                  background: "var(--ids-color-background-primary, #fff)",
                  border: "1px solid var(--ids-color-border-default, #d6d6d6)",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  opacity: contract.inReview ? 0.85 : 1,
                }}
              >
                {/* Contract name + version */}
                <div>
                  <h2 className="ids-heading-s" style={{ marginBottom: "0.25rem" }}>
                    {contract.contractName}
                  </h2>
                  {contract.version && (
                    <span style={{ fontSize: "0.8rem", color: "var(--ids-color-text-secondary, #666)" }}>
                      Version {contract.version}
                    </span>
                  )}
                </div>

                {/* Status badge */}
                <div>{getStatusBadge(contract)}</div>

                {/* Action button */}
                <IDSButton
                  size="s"
                  onClick={() => handleSelectContract(contract)}
                  style={{ alignSelf: "flex-start", marginTop: "0.5rem" }}
                >
                  Starta testning
                </IDSButton>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ContractSelection;
